import {ChevronRightIcon} from '@primer/octicons-react'
import {Button, ProgressBar, type ProgressBarProps, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {MouseEvent, PropsWithChildren} from 'react'
import styles from './MiniMergeSections.module.css'
import {MiniStatusIcon, type MiniStatusIconProps} from './MiniStatusIcon'

function MiniMergeStatusItem({'aria-label': ariaLabel, children}: PropsWithChildren<{'aria-label': string}>) {
  return (
    <section className={styles.MiniStatus} aria-label={ariaLabel}>
      {children}
    </section>
  )
}

type LabelProps = PropsWithChildren<{
  icon?: MiniStatusIconProps['type']
  isUnavailable?: boolean
  successful?: number
  status?: string
  total?: number
}>

function Label({icon, isUnavailable = false, successful, status, total, children}: LabelProps) {
  const hasRatio = successful !== undefined && total !== undefined
  const iconType = isUnavailable ? 'empty' : icon
  const ratioText = hasRatio ? (isUnavailable || total === 0 ? '0' : `${successful}/${total}`) : undefined

  return (
    <span className={clsx('d-flex flex-items-center gap-1', styles.MiniStatusLabel)}>
      {iconType ? <MiniStatusIcon type={iconType} /> : null}
      <strong className={styles.MiniStatusLabelText}>{children}</strong>
      {ratioText !== undefined ? (
        <span className={clsx('fgColor-muted', styles.MiniStatusRatio)}>{ratioText}</span>
      ) : null}
      {status ? <VisuallyHidden>{status}</VisuallyHidden> : null}
    </span>
  )
}

type LabelRowProps = PropsWithChildren<{
  'aria-label'?: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
}>

function LabelRow({'aria-label': ariaLabel, children, onClick}: LabelRowProps) {
  if (!onClick) {
    return <div className={clsx(styles.MiniStatusLabelRow, styles.MiniStatusLabelRowStatic)}>{children}</div>
  }

  return (
    <Button
      alignContent="start"
      aria-label={ariaLabel}
      block
      className={styles.MiniStatusLabelRow}
      onClick={onClick}
      size="small"
      trailingAction={ChevronRightIcon}
      variant="invisible"
    >
      {children}
    </Button>
  )
}

function Progress({
  'aria-label': ariaLabel,
  isUnavailable,
  successful,
  total,
  unavailableLabel,
}: Pick<ProgressBarProps, 'aria-label'> & {
  isUnavailable: boolean
  successful: number
  total: number
  unavailableLabel: string
}) {
  if (isUnavailable) {
    return (
      <div
        className={clsx(styles.MiniStatusProgress, styles.MiniStatusUnavailableProgress)}
        data-testid="mini-status-unavailable-progress"
      >
        <span className="sr-only">{unavailableLabel}</span>
      </div>
    )
  }

  const isEmpty = total === 0

  return (
    <ProgressBar
      aria-label={ariaLabel}
      aria-valuenow={isEmpty ? 0 : undefined}
      bg={isEmpty ? 'neutral.emphasis' : undefined}
      className={styles.MiniStatusProgress}
      progress={isEmpty ? 100 : (successful / total) * 100}
    />
  )
}

MiniMergeStatusItem.LabelRow = LabelRow
MiniMergeStatusItem.Label = Label
MiniMergeStatusItem.Progress = Progress

export {MiniMergeStatusItem}
